import apiClient from '@/lib/axios';
import type { ApiResponse } from '@/features/leads/types';

/**
 * Request payload for checking duplicate job costs
 */
export interface CostDuplicateCheckRequest {
  amount: number;
  paidTo: string;
  paymentDate: string;
  excludeCostId?: number;
}

/**
 * Request payload for checking duplicate job charges
 */
export interface ChargeDuplicateCheckRequest {
  amount: number;
  description?: string;
  chargeDate: string;
  excludeChargeId?: number;
}

/**
 * Request payload for checking duplicate job payments
 */
export interface PaymentDuplicateCheckRequest {
  amount: number;
  referenceNumber?: string;
  paymentDate: string;
  excludePaymentId?: number;
}

/**
 * Potential duplicate job entry returned from the API
 */
export interface PotentialJobEntryDuplicate {
  id: number;
  amount: number;
  date: string;
  displayInfo: string;
  matchReasons: string[];
}

/**
 * Response from the duplicate check API
 */
export interface JobEntryDuplicateResult {
  hasDuplicates: boolean;
  duplicates: PotentialJobEntryDuplicate[];
}

/**
 * Check for potential duplicate job costs.
 * Matches on: same job + same amount + same paid to + same date (within 1 day)
 *
 * @param jobId - Job ID to check within
 * @param data - Cost information to check for duplicates
 * @returns Promise containing duplicate check results
 */
export const checkCostDuplicates = async (
  jobId: number,
  data: CostDuplicateCheckRequest
): Promise<JobEntryDuplicateResult> => {
  const response = await apiClient.post<ApiResponse<JobEntryDuplicateResult>>(
    `/jobs/${jobId}/costs/check-duplicates`,
    data
  );
  return response.data.data;
};

/**
 * Check for potential duplicate job charges.
 * Matches on: same job + same amount + similar description + same date (within 1 day)
 *
 * @param jobId - Job ID to check within
 * @param data - Charge information to check for duplicates
 * @returns Promise containing duplicate check results
 */
export const checkChargeDuplicates = async (
  jobId: number,
  data: ChargeDuplicateCheckRequest
): Promise<JobEntryDuplicateResult> => {
  const response = await apiClient.post<ApiResponse<JobEntryDuplicateResult>>(
    `/jobs/${jobId}/charges/check-duplicates`,
    data
  );
  return response.data.data;
};

/**
 * Check for potential duplicate job payments.
 * Matches on: same job + same amount + (same reference number OR same date within 1 day)
 *
 * @param jobId - Job ID to check within
 * @param data - Payment information to check for duplicates
 * @returns Promise containing duplicate check results
 */
export const checkPaymentDuplicates = async (
  jobId: number,
  data: PaymentDuplicateCheckRequest
): Promise<JobEntryDuplicateResult> => {
  const response = await apiClient.post<ApiResponse<JobEntryDuplicateResult>>(
    `/jobs/${jobId}/payments/check-duplicates`,
    data
  );
  return response.data.data;
};
